import { Box, Modal, PasswordInput, Text } from '@inflearn/ds-react';
import { KoOnly } from '@inflearn/i18n-tools';
import _, { defaults } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';

import type { AccountInventoryResponseDto } from '../../../../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../../../../resources/ApiError';
import useClientAuthWithdrawCreateMutation from '../../../../../../mutations/useClientAuthWithdrawCreateMutation';
import { ERROR_MESSAGE } from '../../../../../../validators/constants';
import { UserInventory } from './UserInventory';
import { InstructorNotice } from './instructor/InstructorNotice';
import { LeaveModalAlert } from './instructor/LeaveModalAlert';
import { LeaveModalFooter } from './instructor/LeaveModalFooter';

interface LeaveModalProps {
  email?: string;
  hasPassword: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isOpened: boolean;
  inventory?: AccountInventoryResponseDto | null;
}

const DEFAULT_USER_INVENTORY = {
  couponCount: 0,
  voucherCount: 0,
  point: 0,
  hasRemainSettlementAmount: false,
};

const LEAVE_ERROR_CODE = {
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  INVALID_ACCOUNT: 'INVALID_ACCOUNT',
  INSTRUCTOR_REMAIN_SETTLEMENT_AMOUNT: 'INSTRUCTOR_REMAIN_SETTLEMENT_AMOUNT',
  CANNOT_WITHDRAW_GROUP_MANAGER: 'CANNOT_WITHDRAW_GROUP_MANAGER',
  BUSINESS_ACCOUNT_NOT_FOUND: 'BUSINESS_ACCOUNT_NOT_FOUND',
  CANNOT_WITHDRAW_UNLIMITED_SUBSCRIPTION: 'CANNOT_WITHDRAW_UNLIMITED_SUBSCRIPTION',
};

/** @NOTE: 정산금있는 지공자인 경우 번역 미적용. 추후 번역 적용 */
export const LeaveModal = ({
  email = '',
  hasPassword,
  onClose,
  isOpened,
  onSuccess,
  inventory,
}: LeaveModalProps) => {
  const { t } = useTranslation();
  const form = useForm<{ password: string }>({
    mode: 'all',
    defaultValues: { password: '' },
  });
  const { isDirty } = form.formState;

  const withDrawMutation = useClientAuthWithdrawCreateMutation({
    onError: (error) => {
      /**
       * @link https://inflab-team.slack.com/archives/C05GZH73DPB/p1743657460977489?thread_ts=1743653383.924929&cid=C05GZH73DPB
       * 탈퇴 케이스별 에러메세지 구분
       */
      if (ApiError.isApiError(error)) {
        if (error.errorCode == LEAVE_ERROR_CODE.ACCOUNT_NOT_FOUND) {
          form.setError('password', {
            type: 'manual',
            message: t('계정이 존재하지 않습니다.'),
          });

          return;
        }

        if (error.errorCode == LEAVE_ERROR_CODE.INVALID_ACCOUNT) {
          form.setError('password', {
            type: 'manual',
            message: t('비밀번호가 일치하지 않습니다. 비밀번호 찾기를 먼저 진행해 주세요.'),
          });

          return;
        }

        if (error.errorCode == LEAVE_ERROR_CODE.CANNOT_WITHDRAW_UNLIMITED_SUBSCRIPTION) {
          form.setError('password', {
            type: 'manual',
            message: t('구독권이 있는 B2B 멤버 계정 탈퇴는 고객 센터로 문의해주세요.'),
          });

          return;
        }

        if (error.errorCode == LEAVE_ERROR_CODE.CANNOT_WITHDRAW_GROUP_MANAGER) {
          form.setError('password', {
            type: 'manual',
            message: t('비즈니스 관리자 계정 탈퇴는 고객 센터로 문의해주세요.'),
          });

          return;
        }

        form.setError('password', {
          type: 'manual',
          message: t('문제가 발생했습니다. 고객 센터로 문의해주세요.'),
        });

        return;
      }

      form.setError('password', {
        type: 'manual',
        message: ERROR_MESSAGE.UNCONFIRMED_PASSWORD()(),
      });
    },
  });

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const handleLeave = ({ password }: { password: string }) => {
    withDrawMutation.mutate(
      { password },
      {
        onSuccess: () => {
          handleClose();
          onSuccess();
        },
      },
    );
  };

  const userInventory = defaults(inventory, DEFAULT_USER_INVENTORY);
  const isInventoryEmpty = _.chain(userInventory).values().sum().value() === 0;
  const showPasswordInput = hasPassword && !userInventory.hasRemainSettlementAmount;

  return (
    <Modal.Root
      opened={isOpened}
      onClose={handleClose}
      size={580}
      styles={{
        inner: {},
        content: {
          width: '100%',
        },
      }}
    >
      <Modal.Overlay />
      <Box component={hasPassword ? 'form' : 'div'} onSubmit={form.handleSubmit(handleLeave)}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{t('탈퇴 전, 안내 사항')}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Box>
              <Box>
                <Text size={14} weight={400} color="gray.9">
                  <Trans
                    i18nKey="1. 계정 탈퇴 시, 인프런과 랠릿 서비스에서 모두 탈퇴됩니다."
                    components={{
                      bold: <Text component="span" color="red.8" weight={600} />,
                    }}
                  />
                </Text>
                <Text size={14} weight={400} color="gray.9">
                  {t('2. 탈퇴시 계정과 관련된 모든 권한이 사라지며 복구할 수 없습니다.')}
                </Text>
                <Text size={14} weight={400} color="gray.9">
                  {t(
                    '3. 직접 작성한 콘텐츠(동영상, 게시물, 댓글 등)는 자동으로 삭제되지 않으며, 만일 삭제를 원하시면 탈퇴 이전에 삭제가 필요합니다.',
                  )}
                </Text>
                <Text size={14} weight={400} color="gray.9">
                  {t(
                    '4. 탈퇴 후 동일한 메일로 재가입이 가능하나, 탈퇴한 계정과 연동되지 않습니다.',
                  )}
                </Text>
                <Text size={14} weight={400} color="gray.9">
                  {t(
                    '5. 탈퇴 후 연동된 소셜 계정 정보도 사라지며 소셜 로그인으로 기존 계정 이용이 불가능합니다.',
                  )}
                </Text>
                <Text size={14} weight={400} color="gray.9">
                  {userInventory.hasRemainSettlementAmount
                    ? '6. 탈퇴 신청서를 작성하면 위 내용에 동의하는 것으로 간주됩니다.'
                    : t(
                        '6. 현재 비밀번호를 입력하고 탈퇴하기를 누르시면 위 내용에 동의하는 것으로 간주됩니다.',
                      )}
                </Text>
              </Box>
              {userInventory.hasRemainSettlementAmount && (
                <KoOnly>
                  <InstructorNotice />
                </KoOnly>
              )}
              {!isInventoryEmpty && (
                <>
                  <UserInventory userInventory={userInventory} />
                  <LeaveModalAlert
                    hasRemainSettlementAmount={userInventory.hasRemainSettlementAmount}
                    hasPassword={hasPassword}
                  />
                </>
              )}
              {showPasswordInput && (
                <PasswordInput
                  autoFocus
                  mt={20}
                  label={t('비밀번호')}
                  placeholder={t('현재 비밀번호')}
                  description={t('본인 확인을 위해 현재 계정의 비밀번호를 입력해주세요.')}
                  size="md"
                  error={form.formState.errors.password?.message}
                  {...form.register('password')}
                />
              )}
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <LeaveModalFooter
              hasRemainSettlementAmount={userInventory.hasRemainSettlementAmount}
              hasPassword={hasPassword}
              isDirty={isDirty}
              email={email}
              handleClose={handleClose}
            />
          </Modal.Footer>
        </Modal.Content>
      </Box>
    </Modal.Root>
  );
};
